import CancelCircle from '@kyruus/react-ikons/CancelCircle';
import Theme from '@kyruus/ui-theme';
import useMediaQuery from '@mui/material/useMediaQuery';
import _startCase from 'lodash/startCase';
import React, { useEffect, useState } from 'react';
import { defineMessages, injectIntl } from '@kyruus/intl';
import { withRouter } from 'react-router-dom';
import { DISTANCE_ANY } from '../facet-panel/facet-list/location-facet';
import {
  FacetChip,
  RemoveFilterChipButton,
  StyledChipsContainer
} from '../styles';
import { useFocusManager } from './hooks/useFocusManager';
import { getTabIndex } from './utils';

const messages = defineMessages({
  removeFilterChipLabel: {
    id: 'facet_chips.remove_filter',
    description: 'Accessibility label for removing a search facet chip',
    defaultMessage: 'Remove filter'
  },
  locationFilterChipLabel: {
    id: 'facet_chips.location',
    description: 'Summary of search location information',
    defaultMessage:
      'Within {distance, select, Any {any miles} any {any miles} other {{distance, plural, one {# mile} other {# miles}}}} of {location}'
  }
});

const AppliedChipBaseComponent = ({
  FacetChipComponent,
  filter,
  handleFilterSelection,
  getUpdatedSearch,
  history,
  intl,
  onFocus,
  tabIndex
}) => {
  const facetLabelMessageDescriptor = {
    id: `field.name.${filter.field}`,
    defaultMessage: _startCase(filter.field),
    description: 'The name displayed above the facet'
  };
  const baseFacetMessageDescriptor = {
    id: `field.value.${filter.field}.${filter.term.value}`,
    defaultMessage: `${filter.term.value}`,
    description: 'Facet for provider search'
  };

  const chipLabel = `${intl.formatMessage(
    facetLabelMessageDescriptor
  )}: ${intl.formatMessage(baseFacetMessageDescriptor)}`;

  const removeChipLabel = intl.formatMessage(messages.removeFilterChipLabel);

  const id = `filter-chip-${filter.field}-${filter.term.value}`;

  const handleDelete = () => {
    history.push(
      getUpdatedSearch([
        {
          action: 'delete_key_value',
          key: 'filter',
          value: `${filter.field}:${filter.term.value}`
        }
      ])
    );
  };

  return (
    <FacetChipComponent
      color="primary"
      role="group"
      tabIndex={-1}
      data-testid={id}
      label={
        <span aria-hidden="true" id={id}>
          <span>{chipLabel}</span>
        </span>
      }
      onClick={handleFilterSelection && (() => handleFilterSelection())}
      onDelete={handleDelete}
      deleteIcon={
        <RemoveFilterChipButton
          mode="primary"
          role="button"
          onFocus={onFocus}
          tabIndex={tabIndex}
          aria-describedby={id}
          aria-label={removeChipLabel}
        >
          <CancelCircle
            size="1.6em"
            aria-hidden="true"
            title={removeChipLabel}
          />
        </RemoveFilterChipButton>
      }
    />
  );
};

const AppliedChipBase = (props) => (
  <AppliedChipBaseComponent FacetChipComponent={FacetChip} {...props} />
);

const LocationAppliedChipBaseComponent = ({
  FacetChipComponent,
  getUpdatedSearch,
  history,
  intl,
  onFocus,
  searchSummary,
  tabIndex,
  handleFilterSelection
}) => {
  const chipLabel = intl.formatMessage(messages.locationFilterChipLabel, {
    location: searchSummary.display_location ?? searchSummary.location,
    distance: searchSummary.distance || DISTANCE_ANY
  });
  const removeChipLabel = intl.formatMessage(messages.removeFilterChipLabel);

  const handleDelete = () =>
    history.push(
      getUpdatedSearch([
        { action: 'delete_key', key: 'display_location' },
        { action: 'delete_key', key: 'location' },
        { action: 'delete_key', key: 'distance' }
      ])
    );

  return (
    <FacetChipComponent
      color="primary"
      role="group"
      tabIndex={-1}
      data-testid="filter-chip-location"
      label={
        <span aria-hidden="true" id="filter-chip-location-content">
          {chipLabel}
        </span>
      }
      onClick={handleFilterSelection && (() => handleFilterSelection())}
      onDelete={handleDelete}
      deleteIcon={
        <RemoveFilterChipButton
          mode="primary"
          role="button"
          onFocus={onFocus}
          tabIndex={tabIndex}
          aria-describedby="filter-chip-location-content"
          aria-label={removeChipLabel}
        >
          <CancelCircle
            size="1.6em"
            aria-hidden="true"
            title={removeChipLabel}
          />
        </RemoveFilterChipButton>
      }
    />
  );
};

const LocationAppliedChipBase = (props) => (
  <LocationAppliedChipBaseComponent FacetChipComponent={FacetChip} {...props} />
);

const AppliedChip = withRouter(injectIntl(AppliedChipBase));
const LocationAppliedChip = withRouter(injectIntl(LocationAppliedChipBase));

const isHiddenFacet = (facet) =>
  [
    'location',
    'locations.primary_marketable_location_id',
    'locations.associated_marketable_location_ids',
    'provider.id'
  ].includes(facet.field);

const hasTermsAvailable = (facet) => !!facet?.terms?.length;

const shouldShowFacet = (facet) =>
  !isHiddenFacet(facet) && hasTermsAvailable(facet);

const FacetChipsComponent = ({
  AppliedChipComponent,
  LocationAppliedChipComponent,
  FiltersComponent,
  updatedFilterBar,
  facets,
  getUpdatedSearch,
  searchSummary,
  log
}) => {
  const [activeChip, setActiveChip] = useState(0);
  const { 'aria-orientation': ariaOrientation, onKeyDown } = useFocusManager({
    orientation: 'horizontal',
    itemElementRole: 'button'
  });
  const isScreenMediumOrBelow = useMediaQuery(
    `(max-width:${Theme.screen_medium})`
  );

  const unavailableFacets = facets.filter((facet) => {
    return shouldShowFacet(facet) && facet.terms.some((term) => term.applied);
  });

  const availableFacets = facets.filter((facet) => {
    if (updatedFilterBar) {
      return (
        shouldShowFacet(facet) && !facet.terms.some((term) => term.applied)
      );
    }
    return shouldShowFacet(facet) && facet.terms.some((term) => term.applied);
  });

  let appliedFilters;

  if (updatedFilterBar) {
    appliedFilters = unavailableFacets.reduce((acc, { field, terms }) => {
      const appliedTerms = terms.filter((term) => term.applied);
      if (appliedTerms.length) {
        acc.push({ field, appliedTerms });
      }
      return acc;
    }, []);
  } else {
    appliedFilters = availableFacets.reduce((acc, { field, terms }) => {
      terms
        .filter((term) => term.applied)
        .forEach((term) => acc.push({ field, term }));
      return acc;
    }, []);
  }

  // Reset active chip
  useEffect(() => {
    setActiveChip(0);
  }, [appliedFilters.length]);

  const showLocationChip = searchSummary.location;

  const allAppliedFilters = appliedFilters.map((filter, idx) => {
    const index = showLocationChip ? idx + 1 : idx;
    const selected = activeChip === index;
    const tabIndex = getTabIndex(activeChip, selected, index);
    return (
      <AppliedChipComponent
        filter={filter}
        facet={facets.find((facet) => facet.field === filter.field)}
        key={updatedFilterBar ? filter.field : filter.term.filter_param}
        tabIndex={tabIndex}
        getUpdatedSearch={getUpdatedSearch}
        onFocus={() => setActiveChip(index)}
        updatedFilterBar={updatedFilterBar}
      />
    );
  });

  const locationChipIndex = 0;
  const locationChipSelected = activeChip === locationChipIndex;
  const locationChipTabIndex = getTabIndex(activeChip, locationChipSelected, 0);

  const locationAppliedChips = showLocationChip && (
    <LocationAppliedChipComponent
      searchSummary={searchSummary}
      facet={facets.find((facet) => facet.field === 'location')}
      getUpdatedSearch={getUpdatedSearch}
      tabIndex={locationChipTabIndex}
      onFocus={() => setActiveChip(locationChipIndex)}
      log={log}
    />
  );

  return (
    <FiltersComponent
      role="toolbar"
      aria-orientation={ariaOrientation}
      onKeyDown={onKeyDown}
      tabIndex={-1}
      onBlur={() => setActiveChip(0)}
    >
      {isScreenMediumOrBelow || (
        <>
          {locationAppliedChips}
          {allAppliedFilters}
        </>
      )}
    </FiltersComponent>
  );
};

const FacetChips = (props) => (
  <FacetChipsComponent
    AppliedChipComponent={AppliedChip}
    LocationAppliedChipComponent={LocationAppliedChip}
    FiltersComponent={StyledChipsContainer}
    updatedFilterBar={false}
    {...props}
  />
);

export { FacetChips, shouldShowFacet };
